import React, { FC, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faSquare, faSquareCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Todo, TodoEntityType } from "../../api/dto/Todo";
import { useTodoContext } from "../../todos/useTodoContext";
import { NavLink } from "react-router";

// Most specific first: if a todo is linked to an order, that's the most useful single link to
// surface by default over its (also-present) quote/project/relation ancestors.
const ENTITY_TYPES_BY_SPECIFICITY: TodoEntityType[] = ['order', 'quote', 'project', 'relation']

const entityPath = (todo: Todo, type: TodoEntityType): string | null => {
  switch (type) {
    case 'relation':
      return todo.relationId !== null ? `/relations/${todo.relationId}` : null
    case 'project':
      return todo.projectId !== null ? `/projects/${todo.projectId}` : null
    case 'quote':
      return todo.quoteId !== null ? `/quotes/${todo.quoteId}` : null
    case 'order':
      // Orders only have a detail route nested under their quote.
      return (todo.quoteId !== null && todo.orderId !== null) ? `/quotes/${todo.quoteId}/orders/${todo.orderId}` : null
  }
}

const entityLabel = (todo: Todo, type: TodoEntityType): string | null => {
  switch (type) {
    case 'relation':
      return todo.relation !== null ? `Relatie: ${todo.relation.name}` : null
    case 'project':
      return todo.project !== null ? `Project${todo.project.number ? ` #${todo.project.number}` : ''}` : null
    case 'quote':
      return todo.quote !== null ? `Offerte${todo.quote.number ? ` #${todo.quote.number}` : ''}` : null
    case 'order':
      return todo.order !== null ? `Bestelling${todo.order.number ? ` #${todo.order.number}` : ''}` : null
  }
}

interface EntityLabelEntry {
  type: TodoEntityType
  label: string
  path: string | null
}

const EntityLabelLink: FC<{ crmUrl: string, entry: EntityLabelEntry }> = ({ crmUrl, entry }) => {
  return entry.path !== null ? (
    <NavLink to={`${crmUrl}${entry.path}`} className={"hover:underline hover:text-brand-700"}>{entry.label}</NavLink>
  ) : (
    <span>{entry.label}</span>
  )
}

const TodoEntityLabels: FC<{ todo: Todo, excludeType?: TodoEntityType }> = ({ todo, excludeType }) => {
  const { crmUrl } = useTodoContext()
  const [isExpanded, setIsExpanded] = useState(false)

  const labels = ENTITY_TYPES_BY_SPECIFICITY
    .filter(type => type !== excludeType)
    .map(type => ({ type, label: entityLabel(todo, type), path: entityPath(todo, type) }))
    .filter((entry): entry is EntityLabelEntry => entry.label !== null)

  if (labels.length === 0) return null

  const [primary, ...others] = labels

  // Only the single most relevant link is shown by default, to keep the focus on the to-do text
  // itself; the rest is one click away behind the chevron rather than always cluttering the list.
  return <div className={"flex flex-wrap items-center gap-x-1.5 text-xs text-slate-500"}>
    <EntityLabelLink crmUrl={crmUrl} entry={primary} />
    {others.length > 0 && <>
      {isExpanded && others.map(entry => <EntityLabelLink key={entry.type} crmUrl={crmUrl} entry={entry} />)}
      <button onClick={() => setIsExpanded(x => !x)} className={"text-slate-400 hover:text-slate-600"}>
        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className={"text-[10px]"} />
      </button>
    </>}
  </div>
}

export const TodoItem: FC<{ todo: Todo, excludeType?: TodoEntityType }> = ({ todo, excludeType }) => {
  const { toggleTodo, updateTodoText, deleteTodo } = useTodoContext()
  const [isEditing, setIsEditing] = useState(false)
  const [text, setText] = useState(todo.text)
  // Guards against a double save: pressing Enter closes the input (setIsEditing(false)) while it
  // still has focus, which can fire a native blur -> onBlur={saveText} a second time.
  const isSavingRef = useRef(false)

  const startEditing = () => {
    // Resync with the latest text in case it changed elsewhere (e.g. edited in another tab) since
    // this component last rendered its own local `text` state.
    setText(todo.text)
    setIsEditing(true)
  }

  const saveText = async () => {
    if (isSavingRef.current) return
    const trimmed = text.trim()
    if (trimmed.length === 0 || trimmed === todo.text) {
      setIsEditing(false)
      setText(todo.text)
      return
    }
    isSavingRef.current = true
    try {
      await updateTodoText(todo, trimmed)
      setIsEditing(false)
    } catch {
      // Keep the edit field open with the typed text so the user can retry, instead of silently
      // dropping the edit.
    } finally {
      isSavingRef.current = false
    }
  }

  return <div className={"flex items-start space-x-2 px-1 min-h-8 group border border-transparent hover:border-slate-200 rounded-sm"}>
    <button onClick={() => toggleTodo(todo)} className={`mt-1 ${todo.isDone ? 'text-brand-900' : 'text-slate-200'}`}>
      <FontAwesomeIcon icon={todo.isDone ? faSquareCheck : faSquare} />
    </button>
    <div className={"flex-1 my-1"}>
      {isEditing ? (
        <input
          autoFocus
          className={"w-full h-6 text-sm border-b border-slate-300 outline-none"}
          value={text}
          onChange={e => setText(e.target.value)}
          onBlur={saveText}
          onKeyDown={e => {
            if (e.key === 'Enter') void saveText()
          }}
        />
      ) : (
        <div
          className={`text-sm min-h-6 flex items-center cursor-text ${todo.isDone ? 'line-through text-slate-400' : ''}`}
          onClick={startEditing}
        >
          {todo.text}
        </div>
      )}
      <TodoEntityLabels todo={todo} excludeType={excludeType} />
    </div>
    <button
      className={"text-sm opacity-25 mt-1 grayscale group-hover:grayscale-0 group-hover:opacity-100"}
      onClick={() => deleteTodo(todo.id)}
    >
      <FontAwesomeIcon icon={faTimes} className={"px-1.5 py-1 rounded-sm text-red-600 hover:bg-red-100"} />
    </button>
  </div>
}
